import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { ExampleLines, formatIPA, playPronunciation } from "../flashcardUtils";

function AddCardDialog({
  open,
  selectedDeck,
  cardForm,
  wordSuggestionsQuery,
  lookupWordMutation,
  onClose,
  onCreateCard,
  onLookupWord,
  onSetCardForm,
}) {
  if (!selectedDeck) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: { xs: "18px", sm: "22px" },
          bgcolor: "var(--toeic-surface)",
          boxShadow: "0 26px 60px -32px rgba(19, 43, 63, 0.55)",
          m: { xs: 1.5, sm: 3 },
          maxHeight: { xs: "calc(100dvh - 24px)", sm: "min(760px, calc(100dvh - 48px))" },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ color: "var(--toeic-ink-deep)", fontWeight: 700, fontSize: 20 }}>
          Add card to {selectedDeck.name}
        </Typography>
        <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 13, fontWeight: 500 }}>
          Search a word, review the suggested IPA and meaning, then save.
        </Typography>
      </DialogTitle>
      <DialogContent
        className="lingualica-scrollarea"
        sx={{ pt: "8px !important", pb: 2 }}
      >
        <Box component="form" id="flashcard-card-form" onSubmit={onCreateCard} sx={{ display: "grid", gap: 1.5 }}>
          <Autocomplete
            freeSolo
            options={wordSuggestionsQuery.data || []}
            loading={wordSuggestionsQuery.isFetching}
            inputValue={cardForm.front}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.word || ""
            }
            filterOptions={(options) => options}
            onInputChange={(_event, value) => {
              onSetCardForm((current) => {
                if (value === current.front) return current;

                return {
                  ...current,
                  front: value,
                  back: "",
                  example: "",
                  ipa: "",
                  audioUrl: "",
                  audioUrls: [],
                  audioRegion: "",
                  partOfSpeech: "",
                  definition: "",
                  lexicalSource: "",
                };
              });
            }}
            onChange={(_event, value) => {
              const word = typeof value === "string" ? value : value?.word;
              if (word) onLookupWord(word);
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.word}>
                <Box>
                  <Typography sx={{ color: "var(--toeic-ink)", fontWeight: 600 }}>
                    {option.word}
                  </Typography>
                  {option.ipa && (
                    <Typography
                      sx={{
                        color: "var(--toeic-ink-soft)",
                        fontFamily: "var(--lingualica-font-ipa)",
                        fontSize: 13,
                      }}
                    >
                      {formatIPA(option.ipa)}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Word or phrase"
                fullWidth
                helperText="Choose a suggestion to fetch pronunciation, meaning, and example."
                onBlur={() => {
                  if (!cardForm.ipa && cardForm.front.trim()) {
                    onLookupWord(cardForm.front);
                  }
                }}
              />
            )}
          />
          {(lookupWordMutation.isPending ||
            cardForm.ipa ||
            cardForm.audioUrl ||
            cardForm.audioUrls?.length ||
            cardForm.definition ||
            cardForm.example ||
            cardForm.lexicalSource) && (
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: "12px",
                border: "1px solid #d7eaf5",
                bgcolor: "#fffefd",
                display: "grid",
                gap: 0.75,
              }}
            >
              {cardForm.partOfSpeech && (
                <Typography
                  sx={{
                    color: "var(--toeic-ink)",
                    fontSize: 13,
                    fontStyle: "italic",
                    fontWeight: 700,
                  }}
                >
                  {cardForm.partOfSpeech}
                </Typography>
              )}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ minHeight: 34, flexWrap: "wrap" }}
              >
                <Typography sx={{ color: "var(--toeic-ink)", fontSize: 13, fontWeight: 800 }}>
                  {cardForm.audioRegion ? cardForm.audioRegion.toUpperCase() : "EN"}
                </Typography>
                <IconButton
                  type="button"
                  size="small"
                  disabled={lookupWordMutation.isPending}
                  onClick={() =>
                    playPronunciation(cardForm.front, [
                      ...(cardForm.audioUrls || []),
                      cardForm.audioUrl,
                    ])
                  }
                  sx={{
                    width: 30,
                    height: 30,
                    color: "var(--toeic-sky-deep)",
                    bgcolor: "#e8f6ff",
                    "&:hover": { bgcolor: "#dceeff" },
                  }}
                >
                  {lookupWordMutation.isPending ? (
                    <CircularProgress size={16} />
                  ) : (
                    <VolumeUpIcon fontSize="small" />
                  )}
                </IconButton>
                <Typography
                  sx={{
                    color: cardForm.ipa ? "var(--toeic-ink)" : "var(--toeic-ink-soft)",
                    fontFamily: "var(--lingualica-font-ipa)",
                    fontSize: 15,
                    fontWeight: 400,
                    lineHeight: 1.4,
                  }}
                >
                  {lookupWordMutation.isPending
                    ? "Looking up pronunciation..."
                    : formatIPA(cardForm.ipa) || "Audio will use browser voice."}
                </Typography>
              </Stack>
              {(cardForm.definition || cardForm.example) && (
                <Box
                  sx={{
                    display: "grid",
                    gap: 0.75,
                    pt: 0.75,
                    borderTop: "1px dashed #d7eaf5",
                  }}
                >
                  {cardForm.definition && (
                    <Box>
                      <Typography
                        sx={{
                          color: "var(--toeic-ink-soft)",
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        Definition
                      </Typography>
                      <Typography
                        sx={{
                          color: "var(--toeic-ink)",
                          fontSize: 13.5,
                          fontWeight: 500,
                          lineHeight: 1.45,
                        }}
                      >
                        {cardForm.definition}
                      </Typography>
                    </Box>
                  )}
                  {cardForm.example && (
                    <Box>
                      <Typography
                        sx={{
                          color: "var(--toeic-ink-soft)",
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        Example
                      </Typography>
                      <ExampleLines
                        example={cardForm.example}
                        term={cardForm.front}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
          <TextField label="Meaning" value={cardForm.back} onChange={(event) => onSetCardForm((current) => ({ ...current, back: event.target.value }))} fullWidth multiline minRows={2} />
          <TextField label="Example sentence" value={cardForm.example} onChange={(event) => onSetCardForm((current) => ({ ...current, example: event.target.value }))} fullWidth multiline minRows={2} />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 0.5 }}>
            <Button type="button" onClick={onClose} sx={{ borderRadius: "999px" }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" startIcon={<AddIcon />} sx={{ borderRadius: "999px", bgcolor: "#6d4aff", "&:hover": { bgcolor: "#5635d6" } }}>
              Add flashcard
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default AddCardDialog;
